// Resource Calculator V3 — engine entry point.
//
// 5-stage direct-table pipeline (the legacy 6th "automation maturity" stage
// was retired alongside the standalone Automation Maturity module — automation
// hour savings are now derived from the Automation Impact catalog on the
// model and applied in the UI on top of time-concentration data):
//
//   Stage 1: total FTE per (discipline × channel)
//            = drivingBudget(basis) / spendPerFte.ratio
//   Stage 2: distribute that total across levels via levelWeights
//   Stage 3: apply per-cell profile-factor multipliers (vertical, budget bucket,
//            campaigns, complexity_*) using the existing targetedFactors infra
//   Stage 4: split each cell into onshore / nearshore / offshore using the
//            per-hub `hubPct` table (aggregated by OgsHub.shore), gated by
//            profile.ogs.* toggles
//   Stage 5: cost each shore-split FTE via costPerRole table
//
// `byDisciplineChannelLevel` is the output grain; `byDiscipline`, `byChannel`,
// `byDisciplineLevel`, `disciplineSummary` and `byShore` are rollups of it.

import type {
  ByChannelRow,
  ByDisciplineLevelCell,
  ByDisciplineRow,
  ByShoreRow,
  CalculationResult,
  CalculationSummary,
  ClientProfile,
  DisciplineChannelCell,
  DisciplineSummaryRow,
  LevelId,
  Model,
  SpendPerFteRow,
  Stage2Row,
  Stage3Row,
  Stage4Cell,
} from './types.js';
import { EPSILON } from './stages/helpers.js';
import { LEVEL_IDS } from './types.js';
import { exactLevelLabel } from './stages/level-groups.js';
import { lookupCostPerRole } from './stages/table-lookup.js';
import { distributeByLevel, resolveLevelWeights } from './stages/level-distribution.js';
import {
  profileFactorMultiplier,
  withDerivedDimensions,
} from './stages/profile-factors.js';
import { splitShore } from './stages/shore-split.js';
import { costForCell } from './stages/costing.js';
import { modelCurrency } from './currency.js';
import { FTE_CAPACITY } from './constants.js';

export { FTE_CAPACITY } from './constants.js';

// ---------------------------------------------------------------------------
// Public API
//
// This barrel deliberately re-exports only what the apps and routes need.
// Anything not listed here is considered an engine internal — keep changes
// isolated to `packages/engine/src/**` and feel free to refactor freely.
// ---------------------------------------------------------------------------

// Types — full type surface remains public so consumers can name model /
// scenario / result shapes directly from the package root.
export * from './types.js';
export * from './clientConstants.js';
export * from './currency.js';

// Lookup helpers used by result-view UI cards.
export {
  lookupOffshorePct,
  lookupNearshorePct,
  lookupHubPct,
  defaultHubForShore,
  lookupCostPerRole,
  timeConcentrationFor,
  deriveDefaultOgsForDiscipline,
  isOgsEligible,
} from './stages/table-lookup.js';

// Profile-factor derivation used by the intake form.
export {
  deriveBudgetBucketId,
  deriveLobCountId,
} from './stages/profile-factors.js';

// Staff plan parser — AI request / reply contract for staff plan uploads.
export {
  DEFAULT_STAFF_PLAN_PARSER_SKILL,
  staffPlanParserSkill,
  staffPlanChannelsForDiscipline,
  buildStaffPlanParserInput,
  buildStaffPlanParserRequest,
  parseStaffPlanParserReply,
  normalizeStaffPlanParseResult,
  type StaffPlanWorkbook,
  type StaffPlanWorkbookSheet,
  type StaffPlanParserInput,
  type StaffPlanParserRequest,
  type StaffPlanParseResult,
} from './staff-plan-parser/index.js';

// Job level groups — the user-facing level grain.
export {
  LEVEL_GROUPS,
  levelGroupOf,
  levelGroupById,
  exactLevelLabel,
  groupsForLevels,
  levelsForGroups,
  sumByLevelGroup,
  averageForLevelGroup,
  scaleLevelGroup,
} from './stages/level-groups.js';
export type { LevelGroup, LevelGroupId } from './stages/level-groups.js';

// Leveling — seniority tilt + preview used by the intake form.
export { SENIORITY_TILT_STRENGTH } from './stages/level-buckets.js';
export {
  resolveLevelWeights,
  resolveLevelShift,
  levelMixPreview,
} from './stages/level-distribution.js';

// Staff-plan helpers used by the API + staff-plan UI.
export {
  flattenStaffPlanToByLevel,
  totalsForStaffPlan,
  summarizeOtherRoles,
  resolveCellSplit,
  resolveRowHub,
  isHubValid,
  staffPlanRowKey,
  staffPlanCellKey,
  isActualStaffPlan,
  staffPlanEntrySource,
  staffPlanEntrySourceLabel,
  applyStaffPlanOverrides,
  staffPlanToBasisCells,
  staffPlanToOgsAdjustedBasisCells,
  basisCellsToStaffPlanCells,
  scenarioWorkspace,
} from './stages/staff-plan.js';
export type {
  AdjustedStaffPlan,
  StaffPlanCellDrift,
} from './stages/staff-plan.js';

// Automation release math — shared by the Automation Impact tab, the Summary
// dollar tiles and the after-automation matrix so the three agree by construction.
export {
  AUTOMATION_HORIZON_MONTHS,
  AUTOMATION_DEFAULT_RAMP_MONTHS,
  AUTOMATION_MIN_RAMP_MONTHS,
  AUTOMATION_MAX_RAMP_MONTHS,
  resolveAutomationDeployment,
  resolveAutomationDeployments,
  automationAdoption,
  automationHorizonLabel,
  automationHorizonWords,
  staffPlanHorizonFor,
  staffPlanHorizonWords,
  mapCellToSubDiscKey,
  basisChannelLabel,
  automationCellImpacts,
  automationTotals,
  automationReleaseByCell,
  automationResidualFte,
} from './stages/automation.js';
export {
  scenarioAutomations,
  automationSolutionProfilePatch,
  clearedAutomationSolutionPatch,
  normalizeAutomationSet,
} from './stages/automation-sets.js';
export type { NormalizedAutomationSet } from './stages/automation-sets.js';
export type {
  AutomationHorizon,
  AutomationDeployment,
  AutomationCellImpact,
  AutomationTotals,
  AutomationCellRelease,
} from './stages/automation.js';

export {
  evaluateActivationConditions,
} from './stages/helpers.js';

// OMCL taxonomy — referenced by time-concentration / automation-impact /
// heatmap UI and the automation-impacts editor.
export {
  NON_CAMPAIGN_PHASE_ID,
  OMCL_PHASES,
  OMCL_SUB_PHASES,
  SUB_DISCIPLINES,
  isNonCampaignSubPhase,
  parentDisciplineFor,
  getSubPhase,
  getSubDiscipline,
  subPhasesForPhase,
} from './maturity-taxonomy.js';
export type {
  OmclPhase,
  OmclSubPhase,
  SubDiscipline,
} from './maturity-taxonomy.js';


// -----------------------------------------------------------------------------
// Public entry point
// -----------------------------------------------------------------------------

export function runCalculation(model: Model, profileIn: ClientProfile): CalculationResult {
  if (!model || !profileIn) {
    throw new Error('runCalculation requires both a model and a clientProfile');
  }

  const profile = withDerivedDimensions(model, profileIn);
  const fteCap = FTE_CAPACITY;

  const totalBudget = Number(profile.totalBudget) || 0;
  const allocation = profile.channelAllocation || {};

  // ---------------------------------------------------------------------------
  // Stage 1 + 2 + 3: total FTE per (disc, channel), distribute by level, apply factors
  // ---------------------------------------------------------------------------
  const cells: DisciplineChannelCell[] = [];
  const stage4Cells: Stage4Cell[] = [];
  const stage2: Stage2Row[] = [];
  const stage3: Stage3Row[] = [];

  for (const disc of model.disciplines || []) {
    if (disc.active === false) continue;

    const spendRows: SpendPerFteRow[] = (model.spendPerFte || []).filter(
      (r) => r.disciplineId === disc.id,
    );
    if (spendRows.length === 0) continue;

    const discL1Channels: Stage2Row['channelBreakdown'] = [];
    let totalDiscFteForStage2 = 0;
    const pyramidAggregate: Partial<Record<LevelId, number>> = {};

    for (const sp of spendRows) {
      const ratio = Number(sp.ratio) || 0;
      if (ratio <= 0) continue;

      const drivingBudget = drivingBudgetFor(model, profile, sp);
      if (drivingBudget <= 0) continue;

      const totalFte = drivingBudget / ratio;

      const weightsRow = resolveLevelWeights(model, profile, disc.id, sp.channelId);
      const perLevel = distributeByLevel(totalFte, weightsRow);

      // For each level cell, apply profile factor multipliers.
      for (const lvl of LEVEL_IDS) {
        const baseFte = Number(perLevel[lvl]) || 0;
        if (baseFte <= EPSILON) continue;

        const tfMult = profileFactorMultiplier(model, profile, disc.id, lvl);
        const adjustedFte = baseFte * tfMult;

        const split = splitShore(model, profile, disc.id, sp.channelId, lvl, adjustedFte);
        const cost = costForCell(model, profile, disc.id, sp.channelId, lvl, split);

        // Stage 6 — automation impact is handled outside the model engine
        // for now (per-discipline automation potential was removed). The
        // engine reports a 0 reduction; downstream automation factor will
        // be reintroduced when the new approach is wired in.
        const sAutoFte = 0;
        const postAutomationFte = adjustedFte;

        cells.push({
          disciplineId: disc.id,
          disciplineLabel: disc.label,
          channelId: sp.channelId,
          channelLabel:
            sp.channelId === 'all'
              ? 'All channels'
              : (model.channels || []).find((c) => c.id === sp.channelId)?.label || sp.channelId,
          levelId: lvl,
          levelLabel: exactLevelLabel(lvl),
          baseFte,
          adjustedFte,
          onshoreFte: split.onshore,
          nearshoreFte: split.nearshore,
          offshoreFte: split.offshore,
          onshoreCost: cost.onshore,
          nearshoreCost: cost.nearshore,
          offshoreCost: cost.offshore,
          totalCost: cost.total,
          sAutoFte,
          postAutomationFte,
        });

        stage4Cells.push({
          disciplineId: disc.id,
          disciplineLabel: disc.label,
          levelId: lvl,
          levelLabel: exactLevelLabel(lvl),
          baseFtePyramid: baseFte,
          targetedMult: tfMult,
          adjustedFte,
        });

        pyramidAggregate[lvl] = (pyramidAggregate[lvl] || 0) + baseFte;
      }

      // Track L1 per channel for the legacy stage2 row (used by byChannel rollup).
      const l1ForChannel = Number(perLevel.L1) || 0;
      totalDiscFteForStage2 += totalFte;
      if (sp.basis === 'channelBudget' && sp.channelId !== 'all') {
        const channelDef = (model.channels || []).find((c) => c.id === sp.channelId);
        discL1Channels.push({
          channelId: sp.channelId,
          channelLabel: channelDef?.label || sp.channelId,
          category: channelDef?.category || 'addressable',
          share: Number(allocation[sp.channelId]) || 0,
          dollars: drivingBudget,
          l1: l1ForChannel,
        });
      }
    }

    stage2.push({
      disciplineId: disc.id,
      disciplineLabel: disc.label,
      driver: 'totalBudget',
      l1Raw: Number(pyramidAggregate.L1) || 0,
      l1: Number(pyramidAggregate.L1) || 0,
      drivingBudget: totalBudget,
      activationFraction: 1,
      activationApplied: false,
      channelBreakdown: discL1Channels,
    });
    const pyramidTotal = Object.values(pyramidAggregate).reduce<number>(
      (s, v) => s + (Number(v) || 0),
      0,
    );
    stage3.push({
      disciplineId: disc.id,
      disciplineLabel: disc.label,
      pyramid: { ...pyramidAggregate, total: pyramidTotal },
    });
  }

  // ---------------------------------------------------------------------------
  // Roll up the cells (byDisciplineLevel, byDiscipline, etc.)
  // ---------------------------------------------------------------------------
  const byDisciplineLevel: ByDisciplineLevelCell[] = [];
  const byDiscipline: ByDisciplineRow[] = [];

  // Group cells by discipline + level (sum across channels).
  const groupKey = (d: string, l: string) => `${d}::${l}`;
  type LevelAccum = {
    disciplineId: string;
    disciplineLabel: string;
    levelId: LevelId;
    levelLabel: string;
    baseFte: number;
    adjustedFte: number;
    onshoreFte: number;
    nearshoreFte: number;
    offshoreFte: number;
    onshoreCost: number;
    nearshoreCost: number;
    offshoreCost: number;
    sAutoFte: number;
    targetedMultSum: number;
    targetedMultCount: number;
  };
  const levelGroups = new Map<string, LevelAccum>();
  for (const c of cells) {
    const k = groupKey(c.disciplineId, c.levelId);
    const acc =
      levelGroups.get(k) ||
      ({
        disciplineId: c.disciplineId,
        disciplineLabel: c.disciplineLabel,
        levelId: c.levelId,
        levelLabel: c.levelLabel,
        baseFte: 0,
        adjustedFte: 0,
        onshoreFte: 0,
        nearshoreFte: 0,
        offshoreFte: 0,
        onshoreCost: 0,
        nearshoreCost: 0,
        offshoreCost: 0,
        sAutoFte: 0,
        targetedMultSum: 0,
        targetedMultCount: 0,
      } as LevelAccum);
    acc.baseFte += c.baseFte;
    acc.adjustedFte += c.adjustedFte;
    acc.onshoreFte += c.onshoreFte;
    acc.nearshoreFte += c.nearshoreFte;
    acc.offshoreFte += c.offshoreFte;
    acc.onshoreCost += c.onshoreCost;
    acc.nearshoreCost += c.nearshoreCost;
    acc.offshoreCost += c.offshoreCost;
    acc.sAutoFte += c.sAutoFte;
    levelGroups.set(k, acc);
  }
  // attach stage4 mults averaged (information only)
  for (const s of stage4Cells) {
    const k = groupKey(s.disciplineId, s.levelId);
    const acc = levelGroups.get(k);
    if (!acc) continue;
    acc.targetedMultSum += s.targetedMult;
    acc.targetedMultCount += 1;
  }

  for (const disc of model.disciplines || []) {
    if (disc.active === false) continue;
    const cellsHere = Array.from(levelGroups.values()).filter((c) => c.disciplineId === disc.id);
    const baseFteDisc = cellsHere.reduce((s, c) => s + c.adjustedFte, 0);
    const ogsFteDisc = cellsHere.reduce((s, c) => s + c.offshoreFte + c.nearshoreFte, 0);
    const onshoreFteDisc = cellsHere.reduce((s, c) => s + c.onshoreFte, 0);

    // Automation factor is now handled outside the engine; cells emit 0.
    const sAutoDiscY1 = cellsHere.reduce((s, c) => s + c.sAutoFte, 0);
    const sAutoDiscY3 = sAutoDiscY1;
    const ratioY3 = 1;

    for (const cell of cellsHere) {
      // Dollar opportunity sources from the costPerRole table. We pick the
      // 'all'-channel row first, falling back to whatever exact channel match
      // exists. lookupCostPerRole already handles both directions internally.
      const annualOnshore = lookupCostPerRole(
        model,
        'onshore',
        disc.id,
        'all',
        cell.levelId,
      );
      const annualOffshore = lookupCostPerRole(
        model,
        'offshore',
        disc.id,
        'all',
        cell.levelId,
      );
      const ogsDeltaAnnual = Math.max(0, annualOnshore - annualOffshore);

      const adjFte = cell.adjustedFte;
      const cellSAutoY1 = cell.sAutoFte;
      const cellSAutoY3 = cellSAutoY1 * ratioY3;
      const cellOgsY1 = cell.offshoreFte + cell.nearshoreFte;
      const cellOgsY3 = cellOgsY1; // shore split is structural (table-driven), not year-varying.
      const cellOnshoreY1 = cell.onshoreFte;
      const cellOnshoreY3 = Math.max(0, adjFte - cellSAutoY3 - cellOgsY3);

      const currentFromCell =
        Number(profile.currentStaffByLevel?.[`${disc.id}:${cell.levelId}`]) || 0;
      let currentFte = currentFromCell;
      if (currentFromCell === 0 && profile.currentStaffByDiscipline) {
        const discTotal = Number(profile.currentStaffByDiscipline[disc.id]) || 0;
        if (discTotal > 0 && baseFteDisc > 0) {
          currentFte = discTotal * (adjFte / baseFteDisc);
        }
      }
      const y1Total = cellOnshoreY1 + cellOgsY1;
      const y3Total = cellOnshoreY3 + cellOgsY3;
      const y1Delta = currentFte - y1Total;
      const y3Delta = currentFte - y3Total;
      // Annual dollars saved = released onshore FTE × annual onshore cost
      //                       + shifted-to-OGS FTE × (onshore − offshore) cost.
      const y1Dollar =
        Math.max(0, currentFte - cellOnshoreY1) * annualOnshore +
        cellOgsY1 * ogsDeltaAnnual;
      const y3Dollar =
        Math.max(0, currentFte - cellOnshoreY3) * annualOnshore +
        cellOgsY3 * ogsDeltaAnnual;

      byDisciplineLevel.push({
        disciplineId: disc.id,
        disciplineLabel: disc.label,
        levelId: cell.levelId,
        levelLabel: cell.levelLabel,
        baseFte: cell.baseFte,
        adjustedFte: adjFte,
        targetedMult:
          cell.targetedMultCount > 0 ? cell.targetedMultSum / cell.targetedMultCount : 1,
        sAutoY1: cellSAutoY1,
        ogsY1: cellOgsY1,
        onshoreY1: cellOnshoreY1,
        sAutoY3: cellSAutoY3,
        ogsY3: cellOgsY3,
        onshoreY3: cellOnshoreY3,
        currentFte,
        fteOnshoreY1: cellOnshoreY1,
        fteOgsY1: cellOgsY1,
        y1Total,
        y1Delta,
        y1Dollar,
        fteOnshoreY3: cellOnshoreY3,
        fteOgsY3: cellOgsY3,
        y3Total,
        y3Delta,
        y3Dollar,
        deltaAutoFte: cellSAutoY3 - cellSAutoY1,
        onshoreRate: annualOnshore,
        ogsDeltaRate: ogsDeltaAnnual,
      });
    }

    byDiscipline.push({
      disciplineId: disc.id,
      disciplineLabel: disc.label,
      baseFte: baseFteDisc,
      sAutoFteY1: sAutoDiscY1,
      ogsFteY1: ogsFteDisc,
      onshoreFteY1: onshoreFteDisc,
      sAutoFteY3: sAutoDiscY3,
      ogsFteY3: ogsFteDisc,
      onshoreFteY3: Math.max(0, baseFteDisc - sAutoDiscY3 - ogsFteDisc),
      hReq: baseFteDisc * fteCap,
      sAuto: sAutoDiscY1 * fteCap,
      hOgsTarget: ogsFteDisc * fteCap,
      hOnshoreNet: onshoreFteDisc * fteCap,
      fteReleased: 0,
      currentOnshore: 0,
      pctOfTotal: 0,
    });
  }

  // ---------------------------------------------------------------------------
  // Aggregate totals
  // ---------------------------------------------------------------------------
  const totalBaseFte = byDiscipline.reduce((s, d) => s + d.baseFte, 0);
  const totalSAutoFteY1 = byDiscipline.reduce((s, d) => s + d.sAutoFteY1, 0);
  const totalOgsFteY1 = byDiscipline.reduce((s, d) => s + d.ogsFteY1, 0);
  const totalOnshoreFteY1 = byDiscipline.reduce((s, d) => s + d.onshoreFteY1, 0);
  const totalSAutoFteY3 = byDiscipline.reduce((s, d) => s + d.sAutoFteY3, 0);
  const totalOgsFteY3 = byDiscipline.reduce((s, d) => s + d.ogsFteY3, 0);
  const totalOnshoreFteY3 = byDiscipline.reduce((s, d) => s + d.onshoreFteY3, 0);

  const totalHReq = totalBaseFte * fteCap;
  const totalSAutoY1Hours = totalSAutoFteY1 * fteCap;
  const totalHOgsY1Hours = totalOgsFteY1 * fteCap;
  const totalHOnshoreY1Hours = totalOnshoreFteY1 * fteCap;

  const totalCurrentFte = byDisciplineLevel.reduce((s, c) => s + c.currentFte, 0);
  const totalCurrentOnshoreHours = totalCurrentFte * fteCap;

  byDiscipline.forEach((d) => {
    d.pctOfTotal = totalHReq > 0 ? d.hReq / totalHReq : 0;
    const cellsForD = byDisciplineLevel.filter((c) => c.disciplineId === d.disciplineId);
    d.currentOnshore = cellsForD.reduce((s, c) => s + c.currentFte * fteCap, 0);
    d.fteReleased = cellsForD.reduce(
      (s, c) => s + Math.max(0, c.currentFte - c.fteOnshoreY1),
      0,
    );
  });

  // ---------------------------------------------------------------------------
  // Discipline summary
  // ---------------------------------------------------------------------------
  const disciplineSummary: DisciplineSummaryRow[] = (model.disciplines || [])
    .filter((d) => d.active !== false)
    .map((d) => {
      const cellsForD = byDisciplineLevel.filter((c) => c.disciplineId === d.id);
      const sum = (key: keyof ByDisciplineLevelCell) =>
        cellsForD.reduce((s, c) => s + ((c[key] as number) || 0), 0);
      return {
        disciplineId: d.id,
        disciplineLabel: d.label,
        currentFte: sum('currentFte'),
        y1OnshoreFte: sum('fteOnshoreY1'),
        y1OgsFte: sum('fteOgsY1'),
        y1Total: sum('y1Total'),
        y1Delta: sum('y1Delta'),
        y1Dollar: sum('y1Dollar'),
        y3OnshoreFte: sum('fteOnshoreY3'),
        y3OgsFte: sum('fteOgsY3'),
        y3Total: sum('y3Total'),
        y3Delta: sum('y3Delta'),
        y3Dollar: sum('y3Dollar'),
        deltaAutoFte: sum('deltaAutoFte'),
        activationFraction: 1,
        activationApplied: false,
      };
    });

  // ---------------------------------------------------------------------------
  // By-channel rollup (now includes total FTE + cost per channel)
  // ---------------------------------------------------------------------------
  const channelAccumulator = new Map<string, ByChannelRow>();
  (model.channels || []).forEach((ch) => {
    channelAccumulator.set(ch.id, {
      channelId: ch.id,
      channelLabel: ch.label,
      category: ch.category,
      share: Number(profile.channelAllocation?.[ch.id]) || 0,
      dollars: totalBudget * (Number(profile.channelAllocation?.[ch.id]) || 0),
      l1Allocated: 0,
      totalFte: 0,
      totalCost: 0,
      disciplines: [],
    });
  });
  // L1 allocations from stage2 (channel-budget-driven disciplines).
  stage2.forEach((s) => {
    s.channelBreakdown.forEach((cb) => {
      const acc = channelAccumulator.get(cb.channelId);
      if (acc) {
        acc.l1Allocated += cb.l1;
        acc.disciplines.push({
          disciplineId: s.disciplineId,
          disciplineLabel: s.disciplineLabel,
          l1: cb.l1,
        });
      }
    });
  });
  // FTE + cost from the cell grid.
  for (const c of cells) {
    if (c.channelId === 'all') continue;
    const acc = channelAccumulator.get(c.channelId);
    if (!acc) continue;
    acc.totalFte = (acc.totalFte || 0) + c.adjustedFte;
    acc.totalCost = (acc.totalCost || 0) + c.totalCost;
  }
  const byChannel = Array.from(channelAccumulator.values()).sort((a, b) => b.dollars - a.dollars);

  // ---------------------------------------------------------------------------
  // By-shore rollup
  // ---------------------------------------------------------------------------
  const shoreAcc = { onshore: 0, nearshore: 0, offshore: 0 };
  const shoreCostAcc = { onshore: 0, nearshore: 0, offshore: 0 };
  for (const c of cells) {
    shoreAcc.onshore += c.onshoreFte;
    shoreAcc.nearshore += c.nearshoreFte;
    shoreAcc.offshore += c.offshoreFte;
    shoreCostAcc.onshore += c.onshoreCost;
    shoreCostAcc.nearshore += c.nearshoreCost;
    shoreCostAcc.offshore += c.offshoreCost;
  }
  const byShore: ByShoreRow[] = [
    { shore: 'onshore', label: 'Onshore', fte: shoreAcc.onshore, cost: shoreCostAcc.onshore },
    {
      shore: 'nearshore',
      label: 'Nearshore',
      fte: shoreAcc.nearshore,
      cost: shoreCostAcc.nearshore,
    },
    { shore: 'offshore', label: 'Offshore', fte: shoreAcc.offshore, cost: shoreCostAcc.offshore },
  ];
  const totalCost = shoreCostAcc.onshore + shoreCostAcc.nearshore + shoreCostAcc.offshore;

  const hoursDistribution = {
    automation: totalHReq > 0 ? totalSAutoY1Hours / totalHReq : 0,
    ogs: totalHReq > 0 ? totalHOgsY1Hours / totalHReq : 0,
    onshore: totalHReq > 0 ? totalHOnshoreY1Hours / totalHReq : 0,
  };

  const dollarOppY1 = disciplineSummary.reduce((s, d) => s + d.y1Dollar, 0);
  const dollarOppY3 = disciplineSummary.reduce((s, d) => s + d.y3Dollar, 0);

  const summary: CalculationSummary = {
    totalHReq,
    totalSAuto: totalSAutoY1Hours,
    totalHOgs: totalHOgsY1Hours,
    totalHOnshore: totalHOnshoreY1Hours,
    totalCurrentOnshore: totalCurrentOnshoreHours,
    totalBaseFte,
    totalSAutoFteY1,
    totalOgsFteY1,
    totalOnshoreFteY1,
    totalSAutoFteY3,
    totalOgsFteY3,
    totalOnshoreFteY3,
    totalCurrentFte,
    fteOnshoreReleasedY1: Math.max(0, totalCurrentFte - totalOnshoreFteY1),
    fteOgsY1: totalOgsFteY1,
    dollarOppY1,
    dollarOppY3,
    mAuto: 0,
    mAutoY2: 0,
    mAutoY3: 0,
    mOmni: 0,
    mEnterprisePlatform: 0,
    structuralMult: averageTargetedMult(stage4Cells),
    totalBudget,
    engineMode: 'direct-tables',
    currency: modelCurrency(model).base,
    totalCostOnshore: shoreCostAcc.onshore,
    totalCostNearshore: shoreCostAcc.nearshore,
    totalCostOffshore: shoreCostAcc.offshore,
    totalCost,
    totalFteOnshore: shoreAcc.onshore,
    totalFteNearshore: shoreAcc.nearshore,
    totalFteOffshore: shoreAcc.offshore,
  };

  return {
    summary,
    byDiscipline,
    byDisciplineLevel,
    disciplineSummary,
    hoursDistribution,
    byChannel,
    byShore,
    byDisciplineChannelLevel: cells,
    stages: {
      stage2,
      stage3,
      stage4: stage4Cells,
    },
  };
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Returns the dollar driving budget for a spendPerFte row given the profile. */
function drivingBudgetFor(model: Model, profile: ClientProfile, sp: SpendPerFteRow): number {
  const totalBudget = Number(profile.totalBudget) || 0;
  const alloc = profile.channelAllocation || {};
  if (sp.basis === 'totalBudget') return totalBudget;
  if (sp.basis === 'channelBudget') {
    if (sp.channelId === 'all') {
      // No channel known -> assume total budget (acts as a sum-of-channels stand-in).
      return totalBudget;
    }
    return totalBudget * (Number(alloc[sp.channelId]) || 0);
  }
  if (sp.basis === 'custom') {
    const key = (sp.customBasisKey || '').toLowerCase();
    if (!key) return 0;
    const ids = key.split('+').map((s) => s.trim()).filter(Boolean);
    return ids.reduce((s, id) => s + totalBudget * (Number(alloc[id]) || 0), 0);
  }
  // Unknown — fall back to model default.
  void model;
  return totalBudget;
}

function averageTargetedMult(cells: Stage4Cell[]): number {
  const active = cells.filter((c) => c.baseFtePyramid > 0);
  if (active.length === 0) return 1;
  return active.reduce((s, c) => s + c.targetedMult, 0) / active.length;
}

export function approxEqual(a: number, b: number, tol = 0.5): boolean {
  return Math.abs(a - b) <= tol;
}
